import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { buildSigninUrl } from '../newsintelligence/support/openDiscussions';
import { AuthRouteSkeleton } from '../layout/ReusableComponent/Skeleton/RouteSkeletons';
import { checkAppSession, peekSessionCache, markSessionValid, invalidateSessionCache } from './sessionAuth';

const SessionAuthContext = createContext(null);

export function useSessionAuth() {
  const ctx = useContext(SessionAuthContext);
  if (!ctx) {
    throw new Error('useSessionAuth must be used within SessionAuthProvider');
  }
  return ctx;
}

function resolveStatusAfterCheck(ok) {
  if (ok) return 'authenticated';
  if (peekSessionCache()) return 'authenticated';
  return 'guest';
}

/**
 * One SSO session for the whole SPA — switching products does not re-hit /signin.
 */
export function SessionAuthProvider({ children }) {
  const [status, setStatus] = useState(() => (peekSessionCache() ? 'authenticated' : 'unknown'));

  const refreshSession = useCallback(async ({ force = false } = {}) => {
    const ok = await checkAppSession({ force });
    setStatus(resolveStatusAfterCheck(ok));
    return ok || peekSessionCache();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await checkAppSession();
      if (cancelled) return;
      setStatus(resolveStatusAfterCheck(ok));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onValidated = () => {
      setStatus('authenticated');
    };
    const onEnded = () => {
      invalidateSessionCache();
      setStatus('guest');
    };
    window.addEventListener('bahlus-session-validated', onValidated);
    window.addEventListener('bahlus-session-ended', onEnded);
    return () => {
      window.removeEventListener('bahlus-session-validated', onValidated);
      window.removeEventListener('bahlus-session-ended', onEnded);
    };
  }, []);

  const value = useMemo(
    () => ({
      status,
      isAuthenticated: status === 'authenticated',
      refreshSession,
    }),
    [status, refreshSession]
  );

  return <SessionAuthContext.Provider value={value}>{children}</SessionAuthContext.Provider>;
}

export function RequireAuth({ children }) {
  const location = useLocation();
  const { status } = useSessionAuth();

  if (status === 'unknown') {
    return <AuthRouteSkeleton />;
  }

  if (status === 'guest') {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={buildSigninUrl({ returnTo })} replace />;
  }

  return children;
}
